import React from "react";

export interface SegmentedControlProps {
  options: { value: string | number; label: string; icon?: React.ReactNode; disabled?: boolean; disabledTooltip?: string; }[];
  value: string | number;
  onChange: (value: string | number) => void;
  className?: string;
  size?: "small" | "medium" | "large";
  disabled?: boolean;
  orientation?: "horizontal" | "vertical";
  label?: string;
  theme?: "light" | "dark";
    /**
   * Clase Tailwind opcional para establecer un max-width.
   * @default "max-w-[504px]"
   */
  maxWidthClass?: string;
}

const SIZE_STYLES = {
  small: {
    track: "h-8 rounded-md",
    button: "px-2 py-1 text-xs",
    selected: "rounded-md",
  },
  medium: {
    track: "h-10 rounded-md",
    button: "px-1 py-1.5 text-sm",
    selected: "rounded-md",
  },
  large: {
    track: "h-12 rounded-md",
    button: "px-1 py-2 text-sm",
    selected: "rounded-md",
  },
} as const;

const SegmentedControl: React.FC<SegmentedControlProps> = ({
  options,
  value,
  onChange,
  className = "",
  size = "large",
  disabled = false,
  orientation = "horizontal",
  maxWidthClass = "max-w-[180px]",
  label,
}) => {
  const totalOptions = options.length;
  const sizeStyles = SIZE_STYLES[size];
  // Width percent applied via inline style: building `w-[${pct}%]` as a Tailwind
  // class would be invisible to the JIT scanner and disappear from the bundle.
  const buttonBasis = `${100 / totalOptions}%`;
  return (
    <div className={`flex flex-col ${className} ${maxWidthClass}`}>
      {/* Label (if provided) */}
      {label && <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>}

      {/* Segmented Control */}
      <div
        className={`flex ${
          orientation === "vertical" ? "flex-col w-full" : "flex-row w-full"
        } ${sizeStyles.track} border border-gray-300 bg-gray-100 overflow-hidden`}
      >
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => (!disabled && !option.disabled) && onChange(option.value)}
            style={
              orientation === "vertical"
                ? { flexBasis: buttonBasis }
                : { width: buttonBasis }
            }
            className={`flex-1 ${sizeStyles.button} flex items-center justify-center ${
              value === option.value
                ? `bg-white text-black font-semibold border border-gray-300 ${sizeStyles.selected}`
                : "text-gray-500 hover:bg-gray-200"
            } ${
              (disabled || option.disabled) ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={disabled || option.disabled}
            title={option.disabled ? option.disabledTooltip : undefined}
          >
            {option.icon && <span className="mr-2">{option.icon}</span>}
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SegmentedControl;
